import { useState } from 'react';
import { useSongs } from '@/context/SongContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Check, Loader2 } from 'lucide-react';
import { GUITAR_TUNINGS } from '@/lib/utils';
import { toast } from 'sonner';

const INSTRUMENTS = [
    'Acoustic Guitar',
    'Electric Guitar',
    'Bass Guitar',
    'Ukulele',
    'Piano/Keyboard',
    'Banjo',
    'Mandolin',
    'Other'
];

export default function InstrumentNotes({ songId, onComplete }) {
    const { addFragment } = useSongs();
    const [instrument, setInstrument] = useState('');
    const [tuning, setTuning] = useState('');
    const [customTuning, setCustomTuning] = useState('');
    const [capo, setCapo] = useState('');
    const [chords, setChords] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!instrument && !tuning && !customTuning && !capo && !chords && !notes) {
            toast.error('Please add at least one piece of information');
            return;
        }

        setSaving(true);
        try {
            const finalTuning = tuning === 'custom' ? customTuning : 
                (tuning ? GUITAR_TUNINGS.find(t => t.name === tuning)?.notes : undefined);

            const fragment = await addFragment(songId, {
                type: 'instrument',
                label: instrument || 'Instrument Notes',
                instrument: instrument || undefined,
                tuning: finalTuning || undefined,
                capo: capo ? parseInt(capo) : undefined,
                chords: chords || undefined,
                notes: notes || undefined
            });

            toast.success('Instrument notes saved');
            onComplete(fragment?.id);
        } catch (error) {
            console.error('Error saving instrument notes:', error);
            toast.error('Failed to save notes');
        } finally {
            setSaving(false);
        }
    };

    const showTuningField = ['Acoustic Guitar', 'Electric Guitar', 'Bass Guitar', 'Ukulele', 'Banjo', 'Mandolin'].includes(instrument);
    const showCapoField = ['Acoustic Guitar', 'Electric Guitar', 'Ukulele', 'Banjo'].includes(instrument);

    return (
        <div className="space-y-4 max-w-lg mx-auto">
            <div className="space-y-2">
                <Label>Instrument</Label>
                <Select value={instrument} onValueChange={setInstrument}>
                    <SelectTrigger className="hover:border-primary/50 transition-colors">
                        <SelectValue placeholder="Select instrument" />
                    </SelectTrigger>
                    <SelectContent>
                        {INSTRUMENTS.map((inst) => (
                            <SelectItem key={inst} value={inst}>{inst}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {showTuningField && (
                <div className="space-y-2 animate-fade-in">
                    <Label>Tuning</Label>
                    <Select value={tuning} onValueChange={setTuning}>
                        <SelectTrigger className="hover:border-primary/50 transition-colors">
                            <SelectValue placeholder="Select tuning" />
                        </SelectTrigger>
                        <SelectContent>
                            {GUITAR_TUNINGS.map((t) => (
                                <SelectItem key={t.name} value={t.name}>
                                    {t.name} ({t.notes})
                                </SelectItem>
                            ))}
                            <SelectItem value="custom">Custom tuning</SelectItem>
                        </SelectContent>
                    </Select>
                    {tuning === 'custom' && (
                        <Input
                            value={customTuning}
                            onChange={(e) => setCustomTuning(e.target.value)}
                            placeholder="e.g., D G D G B D"
                            className="mt-2 hover:border-primary/50 transition-colors"
                        />
                    )}
                </div>
            )}

            {showCapoField && (
                <div className="space-y-2 animate-fade-in">
                    <Label>Capo Position</Label>
                    <Select value={capo} onValueChange={setCapo}>
                        <SelectTrigger className="hover:border-primary/50 transition-colors">
                            <SelectValue placeholder="Select capo position" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="0">No capo</SelectItem>
                            {[...Array(12)].map((_, i) => (
                                <SelectItem key={i + 1} value={String(i + 1)}>
                                    Fret {i + 1}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            <div className="space-y-2">
                <Label>Chords</Label>
                <Input
                    value={chords}
                    onChange={(e) => setChords(e.target.value)}
                    placeholder="e.g., Am, C, G, F or Am7, Dm9"
                    className="hover:border-primary/50 transition-colors"
                />
                <p className="text-xs text-muted-foreground">
                    Separate chords with commas or spaces
                </p>
            </div>

            <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Fingerpicking pattern, strumming notes, technique reminders..."
                    className="min-h-[80px] resize-none hover:border-primary/50 transition-colors"
                />
            </div>

            <Button
                onClick={handleSave}
                disabled={saving || (!instrument && !tuning && !customTuning && !capo && !chords && !notes)}
                className="w-full h-12 hover:scale-[1.02] active:scale-[0.98] transition-transform"
            >
                {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                    <Check className="w-4 h-4 mr-2" />
                )}
                Save Notes
            </Button>
        </div>
    );
}